/**
 * Shared H3 Configuration for spatial indexing
 * Used by both Lambda functions and frontend
 * 
 * ARCHITECTURE:
 * - Backend fragments data by H3 resolution 2 (base resolution)
 * - Each Lambda handles a single res-2 file
 * - Frontend always queries at res-2, then applies filtering and aggregation
 * 
 * ZOOM CONFIGURATION:
 * - filterRes: H3 resolution to filter data within the res-2 file (null = no filtering)
 * - aggRes: H3 resolution to aggregate results (null = return raw points)
 * - maxZoom: Maximum zoom level for this configuration (null = default for all higher zooms)
 * 
 * HOW IT WORKS:
 * 1. Frontend determines viewport H3 cells at resolution 2
 * 2. Queries backend for each res-2 cell
 * 3. Backend filters data using filterRes (if specified)
 * 4. Backend aggregates results at aggRes (if specified), otherwise returns raw points
 * 
 * Example with config below:
 * - Zoom 0-7:  Query res-2 cells, no filtering, aggregate at res-4
 * - Zoom 8-10: Query res-2 cells, filter by res-4, aggregate at res-6
 * - Zoom 11-12: Query res-2 cells, filter by res-6, aggregate at res-8
 * - Zoom 13+:  Query res-2 cells, filter by res-6, return raw points
 */

// ============================================================================
// CONFIGURATION - Edit this section only
// ============================================================================

// Map zoom levels to filtering and aggregation settings
const zoomToResolutionMap = [
  { maxZoom: 6.5,  filterRes: null, aggRes: 4 },  // World/continent: no filter, agg at res-4
  { maxZoom: 10, filterRes: 4,    aggRes: 6 },  // Region: filter res-4, agg at res-6
  { maxZoom: 12, filterRes: 6,    aggRes: 8 },  // City: filter res-6, agg at res-8
  { maxZoom: null, filterRes: 8,  aggRes: null }, // Street: filter res-8, raw points
];

// ============================================================================
// AUTO-DERIVED VALUES - Do not edit below this line
// ============================================================================

// Base resolution for data fragmentation (always 2)
const BASE_RESOLUTION = 2;

// Automatically derive all unique resolutions used
const resolutions = [
  BASE_RESOLUTION,
  ...new Set(
    zoomToResolutionMap
      .flatMap(m => [m.filterRes, m.aggRes])
      .filter(r => r !== null)
  )
].sort((a, b) => a - b);

module.exports = {
  // H3 resolutions used in the system
  resolutions,
  
  // Base resolution for data fragmentation
  BASE_RESOLUTION,
  
  // Map zoom levels to filter and aggregation settings
  zoomToResolutionMap,

  /**
   * Get filter and aggregation settings for a given zoom level
   * @param {number} zoom - Map zoom level
   * @returns {{filterRes: number|null, aggRes: number|null}} Filter and aggregation resolutions
   */
  getSettingsForZoom: function(zoom) {
    console.log(`[H3Config] getSettingsForZoom(${zoom})`);
    
    for (const config of this.zoomToResolutionMap) {
      if (config.maxZoom === null || zoom <= config.maxZoom) {
        console.log(`[H3Config] Matched config:`, { maxZoom: config.maxZoom, filterRes: config.filterRes, aggRes: config.aggRes });
        return {
          filterRes: config.filterRes,
          aggRes: config.aggRes
        };
      }
    }
    
    // Fallback to last config (should have maxZoom: null)
    const lastConfig = this.zoomToResolutionMap[this.zoomToResolutionMap.length - 1];
    console.log(`[H3Config] Using fallback config:`, lastConfig);
    return {
      filterRes: lastConfig.filterRes,
      aggRes: lastConfig.aggRes
    };
  },

  /**
   * Get column name for a resolution
   * @param {number} resolution - H3 resolution number
   * @returns {string} Column name (e.g., 'h3_res5')
   */
  getColumnName: function(resolution) {
    return `h3_res${resolution}`;
  },

  /**
   * Get all H3 column names
   * @returns {string[]} Array of column names
   */
  getAllColumnNames: function() {
    return this.resolutions.map(res => this.getColumnName(res));
  },

  /**
   * Get the base resolution (first/coarsest resolution for grouping)
   * @returns {number} Base resolution
   */
  getBaseResolution: function() {
    return this.resolutions[0];
  },

  /**
   * Get the next finer resolution after the given one
   * @param {number} resolution - Current resolution
   * @returns {number|null} Next resolution or null if none
   */
  getNextResolution: function(resolution) {
    const index = this.resolutions.indexOf(resolution);
    return index >= 0 && index < this.resolutions.length - 1 
      ? this.resolutions[index + 1] 
      : null;
  }
};
